/**
 * ===========================================================================
 *  graph.js — the mixer
 * ===========================================================================
 *
 *   sound ──► airLP ──► panner ──► voiceGain ─┬─► bus.in ─► bus.gain ─┐
 *                                             │                       │
 *                                             └─► send ─► verbIn ──┐  │
 *                                                                  ▼  ▼
 *                              convolver ─► verbLP ─► verbReturn ─► masterIn
 *                                                                     │
 *                                              comp ◄─────────────────┘
 *                                                │
 *                                     softclip ──┴─► master ─► destination
 *
 * Four buses (music / sfx / ambience / voice), each with its own gain and its
 * own fixed reverb send for beds; positional one-shots compute their own send
 * from distance so a clang 40 m away is mostly reverb and a clang at 2 m is
 * mostly dry. The compressor on the master is what keeps a 30-body musou sweep
 * from clipping while a single footstep is still audible.
 * ===========================================================================
 */
import { makeNoiseBuffer, makeImpulse, shaperCurve, gainNode, filter } from './synth.js';

export const BUS_NAMES = ['music', 'sfx', 'ambience', 'voice'];

const DEFAULT_BUS_GAIN = { music: 0.20, sfx: 0.72, ambience: 0.30, voice: 0.50 };
const BUS_SEND = { music: 0.08, sfx: 0.0, ambience: 0.30, voice: 0.0 };

/** Speed of sound. A hit 34 m away arrives a tenth of a second late. */
export const SOUND_SPEED = 343;

export function createGraph(ctx, rnd, opts = {}) {
  const master = gainNode(ctx, opts.master ?? 0.72);
  const clip = ctx.createWaveShaper();
  clip.curve = shaperCurve(1.6);
  clip.oversample = '2x';

  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -15;
  comp.knee.value = 10;
  comp.ratio.value = 4.5;
  comp.attack.value = 0.004;
  comp.release.value = 0.16;

  const masterIn = gainNode(ctx, 1);
  masterIn.connect(comp); comp.connect(clip); clip.connect(master);
  master.connect(ctx.destination);

  // --- reverb send bus -----------------------------------------------------
  const verbIn = gainNode(ctx, 1);
  const conv = ctx.createConvolver();
  conv.normalize = true;
  conv.buffer = makeImpulse(ctx, rnd, { seconds: opts.verbSeconds ?? 2.3, decay: 3.0, damping: 0.6 });
  const verbLP = filter(ctx, 'lowpass', 4200, 0.5);
  const verbHP = filter(ctx, 'highpass', 180, 0.6);
  const verbReturn = gainNode(ctx, opts.verb ?? 0.46);
  verbIn.connect(conv); conv.connect(verbHP); verbHP.connect(verbLP);
  verbLP.connect(verbReturn); verbReturn.connect(masterIn);

  // --- buses ---------------------------------------------------------------
  const buses = {};
  for (const n of BUS_NAMES) {
    const inp = gainNode(ctx, 1);
    const g = gainNode(ctx, opts[n] ?? DEFAULT_BUS_GAIN[n]);
    inp.connect(g); g.connect(masterIn);
    const send = gainNode(ctx, BUS_SEND[n]);
    if (BUS_SEND[n] > 0) { g.connect(send); send.connect(verbIn); }
    buses[n] = { name: n, in: inp, gain: g, send, base: opts[n] ?? DEFAULT_BUS_GAIN[n] };
  }

  const noise = {
    white: makeNoiseBuffer(ctx, rnd, 'white', 2.0),
    pink: makeNoiseBuffer(ctx, rnd, 'pink', 2.4),
    brown: makeNoiseBuffer(ctx, rnd, 'brown', 2.4),
  };

  return {
    ctx, master, masterIn, comp, clip, verbIn, verbReturn, conv, buses, noise, rnd,
    /* `buses[n].gain` is the GainNode; the automatable AudioParam is
     * `.gain.gain`. Getting that wrong silently broke unlock() once. */
    setBus(name, v) {
      if (name === 'master') { master.gain.setTargetAtTime(v, ctx.currentTime, 0.02); return; }
      const b = buses[name]; if (!b) return;
      b.gain.gain.setTargetAtTime(Math.max(0, v), ctx.currentTime, 0.02);
    },
    busGain(name) { return name === 'master' ? master.gain.value : (buses[name]?.gain.gain.value ?? 0); },
    dispose() { try { master.disconnect(); } catch { /* already gone */ } },
  };
}

/* ======================================================== spatialisation == */

/**
 * The listener. A cheap manual model rather than PannerNode/HRTF: it costs one
 * StereoPanner + one lowpass per shot instead of a full HRTF convolution, it
 * stays deterministic, and — the reason it matters here — the pan and the
 * distance gain are plain numbers we can write into `audio.debugLog` and
 * assert on from a capture, which a PannerNode's internals are not.
 */
export function createListener() {
  return {
    x: 0, y: 2, z: 0,
    rx: 1, ry: 0, rz: 0,      // right vector (unit, world space)
    fx: 0, fy: 0, fz: -1,     // forward
    /** Copy the transform out of a THREE.Camera without allocating. */
    from(camera) {
      if (!camera) return this;
      const m = camera.matrixWorld?.elements;
      const p = camera.position;
      if (p) { this.x = p.x; this.y = p.y; this.z = p.z; }
      if (m) {
        this.rx = m[0]; this.ry = m[1]; this.rz = m[2];
        this.fx = -m[8]; this.fy = -m[9]; this.fz = -m[10];
      }
      return this;
    },
    set(x, y, z, yaw) {
      this.x = x; this.y = y; this.z = z;
      this.rx = Math.cos(yaw); this.ry = 0; this.rz = -Math.sin(yaw);
      this.fx = Math.sin(yaw); this.fy = 0; this.fz = Math.cos(yaw);
      return this;
    },
  };
}

/* Distance model tunables. `ref` is the distance at which gain is 1. */
const REF = 3.5;
const ROLLOFF = 0.62;
export const MAX_DIST = 96;

/** Inverse-distance rolloff with a soft far cutoff. */
export function distanceGain(d) {
  if (d <= REF) return 1;
  if (d >= MAX_DIST) return 0;
  const g = REF / (REF + ROLLOFF * (d - REF));
  // fade the last 25 % of the range to zero so nothing pops at the boundary
  const edge = d > MAX_DIST * 0.75 ? 1 - (d - MAX_DIST * 0.75) / (MAX_DIST * 0.25) : 1;
  return g * edge;
}

/** Air absorption: the far ridge is dull, the blade in your hand is bright. */
export function airCutoff(d) {
  return Math.max(520, 19000 / (1 + (d * d) / 210));
}

/**
 * Build the per-shot spatial chain and return the node a synth should write
 * into. Fills `out` with {gain, pan, dist, delay} for the debug log.
 */
export function spatial(listener, x, y, z, gain, out) {
  const dx = x - listener.x, dy = y - listener.y, dz = z - listener.z;
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const g = gain * distanceGain(d);
  const inv = d > 0.0001 ? 1 / d : 0;
  const rd = (dx * listener.rx + dy * listener.ry + dz * listener.rz) * inv;
  // near sources stay centred (they are "around" you), far ones pan hard
  const spread = Math.min(1, d / 2.6);
  out.dist = d;
  out.gain = g;
  out.pan = Math.max(-1, Math.min(1, rd * spread * 0.94));
  out.delay = d > 10 ? Math.min(0.42, d / SOUND_SPEED) : 0;
  return out;
}

export function place(graph, bus, x, y, z, listener, gain, out, opts) {
  const ctx = graph.ctx;
  spatial(listener, x, y, z, gain, out);
  const d = out.dist, g = out.gain, pan = out.pan;
  if (g <= 0.0008) return null;                       // inaudible: do not build the graph

  const node = gainNode(ctx, g);
  const pn = ctx.createStereoPanner();
  pn.pan.value = pan;
  const lp = filter(ctx, 'lowpass', airCutoff(d), 0.55);
  lp.connect(pn); pn.connect(node); node.connect(bus.in);

  // distance send: near = dry, far = wet
  const sendAmt = Math.min(0.50, 0.05 + (d / MAX_DIST) * 1.05) * (opts?.verb ?? 1);
  if (sendAmt > 0.02) {
    const s = gainNode(ctx, g * sendAmt);
    pn.connect(s); s.connect(graph.verbIn);
  }
  return lp;
}

/** Non-positional (2D) chain: UI, music stings, the player's own voice. */
export function place2D(graph, bus, gain, pan, out) {
  const ctx = graph.ctx;
  out.dist = 0; out.gain = gain; out.pan = pan || 0; out.delay = 0;
  const node = gainNode(ctx, gain);
  if (pan) {
    const pn = ctx.createStereoPanner();
    pn.pan.value = pan; pn.connect(node); node.connect(bus.in);
    return pn;
  }
  node.connect(bus.in);
  return node;
}
